import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge, type BadgeProps } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/hooks/useToast';
import { dateTime } from '@/lib/format';
import * as api from '@/api/vigia.api';
import type { EstadoAviso, MiembroVigia } from '@/api/vigia.api';

/**
 * EL VIGILANTE DE GRUPOS — si en un grupo le hablan a alguien del equipo, se le avisa por
 * privado desde el WhatsApp del negocio, y se le recuerda una vez a las 2 horas.
 */

const ESTADO: Record<EstadoAviso, { etiqueta: string; variant: BadgeProps['variant'] }> = {
  PENDIENTE: { etiqueta: 'En cola', variant: 'warning' },
  AVISADO: { etiqueta: 'Avisado', variant: 'info' },
  RECORDADO: { etiqueta: 'Recordado', variant: 'warning' },
  RESUELTO: { etiqueta: 'Contestó', variant: 'success' },
  DESCARTADO: { etiqueta: 'Solo lo nombraron', variant: 'default' },
  FALLO: { etiqueta: 'No se pudo', variant: 'danger' },
};

const vacio = (): MiembroVigia => ({ nombre: '', alias: [], waJid: null, duenoDelNumero: false, activo: true });

export default function Vigia() {
  const qc = useQueryClient();
  const toast = useToast();
  const equipo = useQuery({ queryKey: ['vigia', 'equipo'], queryFn: api.verEquipo });
  const avisos = useQuery({ queryKey: ['vigia', 'avisos'], queryFn: api.verAvisos, refetchInterval: 30_000 });
  const grupos = useQuery({ queryKey: ['vigia', 'grupos'], queryFn: api.verGrupos });
  const [borrador, setBorrador] = useState<MiembroVigia[]>([]);
  useEffect(() => setBorrador(equipo.data ?? []), [equipo.data]);

  const guardar = useMutation({
    mutationFn: () => api.guardarEquipo(borrador.filter((m) => m.nombre.trim())),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['vigia'] });
      toast.success('Equipo guardado');
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const cambiarGrupo = useMutation({
    mutationFn: (apagados: string[]) => api.guardarGruposApagados(apagados),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['vigia', 'grupos'] }),
  });

  const editar = (i: number, cambio: Partial<MiembroVigia>) =>
    setBorrador((b) => b.map((m, j) => (j === i ? { ...m, ...cambio } : m)));

  if (equipo.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner size={28} className="text-brand-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Vigilante de grupos"
        description="Si en un grupo le hablan a alguien del equipo, le llega un aviso por privado. Si en 2 horas no contestó allá, se le recuerda una vez."
      />

      <Card className="space-y-3 p-4">
        <div className="flex items-center justify-between gap-2">
          <h2 className="font-semibold">Equipo</h2>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setBorrador((b) => [...b, vacio()])}>
              <Plus size={16} /> Persona
            </Button>
            <Button size="sm" onClick={() => guardar.mutate()} disabled={guardar.isPending}>
              Guardar
            </Button>
          </div>
        </div>
        <p className="text-xs text-slate-500">
          «Lo llaman» son los nombres con que lo mencionan en los grupos, separados por coma. Su WhatsApp se aprende solo la
          primera vez que escribe en un grupo.
        </p>
        <div className="space-y-2">
          {borrador.map((m, i) => (
            <div key={m.id ?? `nuevo-${i}`} className="grid gap-2 rounded-lg border border-surface-border p-3 dark:border-slate-700 md:grid-cols-[1fr_2fr_auto_auto]">
              <Input label="Nombre" value={m.nombre} onChange={(e) => editar(i, { nombre: e.target.value })} />
              <Input
                label="Lo llaman"
                value={m.alias.join(', ')}
                onChange={(e) => editar(i, { alias: e.target.value.split(',').map((a) => a.trim()).filter(Boolean) })}
              />
              <div className="flex flex-col justify-end gap-1 text-sm">
                <Badge variant={m.waJid ? 'success' : 'warning'}>{m.waJid ? 'WhatsApp conocido' : 'Falta su WhatsApp'}</Badge>
                <label className="flex items-center gap-1">
                  <input type="checkbox" checked={m.activo} onChange={(e) => editar(i, { activo: e.target.checked })} /> Recibe avisos
                </label>
              </div>
              <Button variant="ghost" size="sm" aria-label={`Quitar a ${m.nombre}`} onClick={() => setBorrador((b) => b.filter((_, j) => j !== i))}>
                <Trash2 size={16} />
              </Button>
            </div>
          ))}
          {borrador.length === 0 && <p className="text-sm text-slate-500">Sin equipo cargado: el vigilante está apagado.</p>}
        </div>
      </Card>

      <Card className="space-y-2 p-4">
        <h2 className="font-semibold">Avisos</h2>
        {(avisos.data ?? []).length === 0 && <p className="text-sm text-slate-500">Todavía no hay avisos.</p>}
        <ul className="divide-y divide-surface-border dark:divide-slate-700">
          {(avisos.data ?? []).map((a) => (
            <li key={a.id} className="flex flex-col gap-1 py-2 text-sm md:flex-row md:items-center md:gap-3">
              <Badge variant={ESTADO[a.estado].variant}>{ESTADO[a.estado].etiqueta}</Badge>
              <span className="font-medium">{a.miembro.nombre}</span>
              <span className="text-slate-500">en {a.grupoNombre ?? 'un grupo'}, de {a.autor ?? 'alguien'}:</span>
              <span className="min-w-0 flex-1 truncate">{a.resumen ?? a.texto}</span>
              <span className="text-xs text-slate-400">{dateTime(a.createdAt)}</span>
              {a.error && <span className="text-xs text-red-600">{a.error}</span>}
            </li>
          ))}
        </ul>
      </Card>

      <Card className="space-y-2 p-4">
        <h2 className="font-semibold">Grupos que se vigilan</h2>
        {(grupos.data ?? []).length === 0 && <p className="text-sm text-slate-500">Aparecen aquí cuando llega el primer mensaje de cada grupo.</p>}
        <div className="grid gap-1 md:grid-cols-2">
          {(grupos.data ?? []).map((g) => (
            <label key={g.jid} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={g.vigilado}
                disabled={cambiarGrupo.isPending}
                onChange={(e) => {
                  const apagados = (grupos.data ?? []).filter((x) => (x.jid === g.jid ? !e.target.checked : !x.vigilado)).map((x) => x.jid);
                  cambiarGrupo.mutate(apagados);
                }}
              />
              {g.nombre}
            </label>
          ))}
        </div>
      </Card>
    </div>
  );
}
